<template>
  <div>
    <ResponseHandler :serviceResponse="response"></ResponseHandler>

    <Loading :isVisible="isLoading" />

    <v-dialog v-model="addFile" persistent>
      <v-card>
        <v-card-title class="px-6">
          <span class="headline">Upload Users</span>
          <v-spacer></v-spacer>
          <v-select 
            v-if="mappedUploadData.length"
            label="Show"
            dense
            v-model="uploadDataFilter"
            outlined
            hide-details
            :items="['All', 'Valid Only', 'Issues Only']"
            style="max-width: 150px;"
            class="mr-4">
          </v-select>
          <v-btn icon large class="btn-background" @click="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <template v-if="!uploadData">
            <v-alert type="info" text dense>
              Please upload an XLS spreadsheet containing the data to be imported.
              Data will only be loaded from the first tab on the spreadsheet and
              the first row is expected to contain column headings.
            </v-alert>
            <v-row dense>
              <v-col cols="8" dense>
                <v-file-input
                  v-model="inputFile"
                  :accept="fileTypes"
                  ref="inputFile"
                  @change="selectFile"
                  label="Select File"
                ></v-file-input>
              </v-col>
              <v-col cols="4" dense class="d-flex justify-end align-center">
                <v-btn
                  dense
                  class="btn"
                  color="primary"
                  :disabled="!selectedFiles.length > 0 || filesUploading"
                  @click="uploadMulti"
                >
                  Upload
                </v-btn>
              </v-col>
            </v-row>
            <v-row
              dense
              v-for="(progressInfo, index) in progressInfos"
              :key="index"
            >
              <v-col cols="8">
                <div>
                  <span>{{ progressInfo.fileName }}</span>
                </div>
              </v-col>
              <v-col cols="4" v-if="progressInfo" style="display: none">
                <div width="100%">
                  <v-progress-linear
                    max="100"
                    height="15"
                    color="success"
                    v-model="progressInfo.percentage"
                  >
                    <template>
                      {{ Math.ceil(progressInfo.percentage) }}%
                    </template>
                  </v-progress-linear>
                </div>
              </v-col>
            </v-row>
            <v-row align="center">
              <v-col cols="12" sm="12" md="12" align="center">
                <div v-if="filesUploading === true" align="center">
                  <v-progress-circular
                    :width="3"
                    color="green"
                    indeterminate
                  ></v-progress-circular>
                  &nbsp; Please Wait... Upload in Progress
                </div>
              </v-col>
            </v-row>
          </template>
          <template v-else-if="!processing">
            <v-alert v-if="invalidUploadData.length !== 0" type="warning" text>
              There are {{ invalidUploadData.length }} rows with validation issues that cannot be imported.
            </v-alert>
            <v-data-table
              :headers="filteredUploadDataHeaders"
              dense
              :sort-by="sortBy"
              :sort-desc="sortDesc"
              :items="filteredMappedUploadedData"
              hide-default-header
            >
              <template v-slot:header="{ props }">
                <thead class="v-data-table-header">
                  <tr>
                    <th
                      v-for="(head, hi) in props.headers"
                      :key="'h' + hi"
                      @click="setUploadSort(head)"
                      :class="head.sortable === false ? '' : 'sortable'"
                    >
                      <template>
                        <span>
                          {{ head.text }}
                        </span>
                        <v-icon small v-if="head.sortable !== false && head.value === sortBy">{{
                          sortDesc ? "arrow_downward" : "arrow_upward"
                        }}</v-icon>

                        <v-menu offset-y>
                          <template v-slot:activator="{ on, attrs }">
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on: tooltip }">
                                <v-icon v-if="head.source" small v-on="{ ...on, ...tooltip }" v-bind="attrs" class="ml-2">settings</v-icon>
                                <v-icon v-else-if="head.required" small v-on="{ ...on, ...tooltip }" v-bind="attrs" color="warning" class="ml-2">warning</v-icon>
                                <v-icon v-else small v-on="{ ...on, ...tooltip }" v-bind="attrs" class="ml-2">warning</v-icon>
                              </template>
                              <span v-if="head.source">Change source column</span>
                              <span v-else>Select a source column</span>
                            </v-tooltip>
                          </template>
                          <v-list dense>
                            <v-list-item
                              v-for="(uh, uhi) in uploadData.headers"
                              :key="'uh' + uhi"
                              @click="setUploadSource(head, uh)"
                              :input-value="head.source === uh"
                              active-class="v-list-item--highlighted"
                            >
                              <v-list-item-title>
                                {{ uh }}
                              </v-list-item-title>
                            </v-list-item>
                          </v-list>
                        </v-menu>
                      </template>
                    </th>
                    <th></th>
                  </tr>
                </thead>
              </template>
              <template v-slot:item="{ item }">
                <tr>
                  <td v-for="col in filteredUploadDataHeaders" :key="col.value">
                    <v-tooltip bottom v-if="col.value === 'username' && item.useEmailForUsername">
                      <template v-slot:activator="{ on: tooltip }">
                        <v-icon small v-on="{ ...tooltip }" color="info" class="ml-1">info</v-icon>
                      </template>
                      <div>SSO user - using email for username</div>
                    </v-tooltip>
                    <span v-else-if="item[col.value].value.length < 64" v-html="item[col.value].value" style="font-size: 0.8rem;"></span>
                    <v-tooltip bottom v-else-if="item[col.value].valueArr.length">
                      <template v-slot:activator="{ on: tooltip }">
                        <span class="truncate" v-html="item[col.value].value.substring(0, 64) + '...'" v-on="{ ...tooltip }" style="font-size: 0.8rem;"></span>
                      </template>
                      <div v-html="item[col.value].valueArr.join('<br />')"></div>
                    </v-tooltip>
                    <v-tooltip bottom v-else>
                      <template v-slot:activator="{ on: tooltip }">
                        <span class="truncate" v-html="item[col.value].value.substring(0, 64) + '...'" v-on="{ ...tooltip }" style="font-size: 0.8rem;"></span>
                      </template>
                      <div v-html="item[col.value].value"></div>
                    </v-tooltip>
                    <v-tooltip bottom v-if="item[col.value].issues.length !== 0">
                      <template v-slot:activator="{ on: tooltip }">
                        <v-icon small v-on="{ ...tooltip }" color="warning" class="ml-1">warning</v-icon>
                      </template>
                      <div v-html="item[col.value].issues.join('<br />')"></div>
                    </v-tooltip>
                  </td>
                  <td>
                    <v-chip
                      v-if="!isValid(item)"
                      small
                      color="warning"
                      text-color="white"
                    >
                      Issue
                      <v-icon right small>
                        mdi-alert
                      </v-icon>
                    </v-chip>
                    <v-chip
                      v-else-if="item.isNew"
                      small
                      color="green"
                      text-color="white"
                    >
                      New
                      <v-icon right small>
                        mdi-alert-decagram
                      </v-icon>
                    </v-chip>
                    <v-chip
                      v-else
                      small
                      color="blue"
                      text-color="white"
                    >
                      Update
                      <v-icon right small>
                        mdi-account-convert
                      </v-icon>
                    </v-chip>
                    
                  </td>
                </tr>
              </template>
            </v-data-table>
            <v-row>
              <v-col class="d-flex">
                <v-btn color="primary" outlined @click="cancelUpload">
                  Cancel
                </v-btn>
                <v-spacer></v-spacer>
                <v-checkbox
                  v-model="uploadSendEmail"
                  label="Send Account Setup Email"
                  class="mr-4"
                ></v-checkbox>
                <v-btn color="primary"
                  :disabled="validUploadData.length === 0"
                  @click="createUsers">
                  {{ createButtonText }}
                </v-btn>
              </v-col>
            </v-row>
          </template>
          <div v-else>
            <v-progress-linear
              color="primary"
              height="25"
              :value="createProgressPct"
            >
              <strong>Processing {{ createProgress }} of {{ validUploadData.length }}</strong>
            </v-progress-linear>
            <v-alert type="error" v-if="createErrors.length !== 0" class="mt-4" text>
              <span v-html="createErrors.join('<br />')"></span>
            </v-alert>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import ResponseHandler from "@/components/ResponseHandler"; // @ is an alias to /src
import { mapState } from "vuex";
//import utils from "@/common/utils.js";

const findNodeIdByDesc = (id, options) => {
  id = (id?.toLowerCase() || "").trim();

  if (id.includes('>')) {
    const levelArr = id.split('>').map(l => l.trim());
    let opt = null;
    levelArr.forEach(l => {
      opt = options.find(o => o.label.trim().toLowerCase() === l);
      if (opt)
        options = opt.children;
    })
    return opt?.node_id;
  } else {
    return findNodeIdByName(id, options);
  }
}

const findNodeIdByName = (id, options) => {
  if (!options || options.length === 0)
    return null;

  const opt = options.find(o => o.label.trim().toLowerCase() === id);
  if (opt)
    return opt.node_id;
  else {
    for (let i = 0; i < options.length; i++) {
      const id_found = findNodeIdByName(id, options[i].children);
      if (id_found)
        return id_found;
    }
  }
}

export default {
  name: "checkJP",
  components: {
    ResponseHandler,
  },
  props: {
    permissionGroups: { type: Array },
    ssoEnabled: { type: Boolean },
  },
  data: function () {
    return {
      response: null,
      isLoading: false,
      addFile: false,
      inputFile: null,
      fileTypes: ".xls,.xlsx,.ods,.csv",
      selectedFiles: [],
      filesUploading: false,
      progressInfos: [],
      uploadData: null,
      sortBy: "lastname",
      sortDesc: false,
      mappedUploadData: [],
      uploadDataFilter: 'All',
      uploadSendEmail: true,
      processing: false,
      createProgress: 0,
      createErrors: [],
    };
  },
  watch: {
    useEmailForUsername() {
      this.mapAndvalidateUploadData();
    },
    uploadDataHeaders() {
      this.mapAndvalidateUploadData();
    }
  },
  computed: {
    ...mapState({
      config: (state) => state.settings.config,
      hierarchies: (state) => state.hierarchies.hierarchies,
    }),
    restrictUsersToHierarchy() {
      return this.config.settings.some(s => s.setting === 'restrict_users_by_hierarchy' && s.value === 'true');
    },
    uploadDataHeaders() {
      const headers = [
        { text: "First Name", value: "firstname", defaultSource: "first", source: null, sort: this.sortByProperty("value"), required: true },
        { text: "Last Name", value: "lastname", defaultSource: "last", source: null, sort: this.sortByProperty("value"), required: true },
      ]

      if (this.ssoEnabled)
        headers.push({ text: "SSO Only", value: "sso_only", defaultSource: "sso", source: null, sort: this.sortByProperty("value"), required: true })

      headers.push(...[
        { text: "Username", value: "username", defaultSource: "user", source: null, sort: this.sortByProperty("value"), required: true },
        { text: "Email", value: "email", defaultSource: "email", source: null, sort: this.sortByProperty("value"), required: true },
        { text: "Permission", value: "permission", defaultSource: "perm", source: null, sort: this.sortByProperty("value"), required: true },
        { hierarchy_restrict: true, text: "Restrict to Hierarchy", value: "hierarchy_restrict", defaultSource: "restrict", source: null, sort: this.sortByProperty("value") }
      ]);

      headers.push(...this.hierarchies.map(ht => {
        return { hierarchy_restrict: true, text: ht.label, value: `ht_${ht.ht_id}`, ht: ht, defaultSource: ht.label.toLowerCase(), source: null, sort: this.sortByProperty("value") }
      }));

      return headers;
    },
    validUploadData() {
      return this.mappedUploadData.filter(d => this.isValid(d));
    },
    invalidUploadData() {
      return this.mappedUploadData.filter(d => !this.isValid(d));
    },
    createButtonText() {
      const validCount = this.validUploadData.length;
      const createCount = this.validUploadData.filter(u => u.isNew).length;
      const updateCount = validCount - createCount;

      if (createCount && updateCount)
        return `Create ${createCount}, Update ${updateCount} User${validCount > 1 ? 's' : ''}`;
      else if (createCount)
        return `Create ${createCount} User${validCount > 1 ? 's' : ''}`;
      else if (updateCount)
        return `Update ${updateCount} User${validCount > 1 ? 's' : ''}`;
      else
        return `Import Users`;
    },
    filteredMappedUploadedData() {
      if (this.uploadDataFilter === 'Issues Only')
        return this.invalidUploadData;
      else if (this.uploadDataFilter === 'Valid Only')
        return this.validUploadData;
      else
        return this.mappedUploadData;
    },
    ssoUseEmail() {
      return this.config.settings.some(s => s.setting === 'user_admin_store_email_as_username_for_sso' && s.value === 'true');
    },
    useEmailForUsername() {
      return this.ssoEnabled && this.ssoUseEmail;
    },
    showSSOOnlyToggle() {
      if (!this.ssoEnabled)
        return false;

      const allowNonSSOUserCreation = (this.config.settings.find(s => s.setting === 'allow_non_sso_user_creation')?.value || 'true') === 'true';

      return (this.$loginState.isInternal || allowNonSSOUserCreation);
    },
    filteredUploadDataHeaders() {
      return this.uploadDataHeaders
        .filter(h => this.restrictUsersToHierarchy || !h.hierarchy_restrict);
    },
    createProgressPct() {
      return Math.round(100 * this.createProgress / this.validUploadData.length);
    }
  },
  created() {
    this.showAddFile();
  },
  methods: {
    close() {
      this.addFile = false;
      this.$emit("closed");
    },
    uploadMulti() {
      this.filesUploading = true;
      let formData = new FormData();

      for (var i = 0; i < this.selectedFiles.length; i++) {
        let file = this.selectedFiles[i];
        formData.append("files[]", file);
      }

      formData.append("dataSetType", this.dataSetType);

      let options = {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      };

      try {
        axios
          .post("user/uploadUsers", formData, options)
          .then((resp) => {
            this.filesUploading = false;
            if (resp.data.Data) {
              this.uploadData = resp.data.Data;
              this.uploadDataHeaders.forEach(h => {
                h.source = this.uploadData.headers.find(sh => sh.toLowerCase().includes(h.defaultSource));
              });
              this.mapAndvalidateUploadData();
            }
          })
          .catch((err) => {
            this.triggerNotification("Error uploading - " + err, "error");
            this.message = "Could not upload the file:" + err;
            this.filesUploading = false;
          });
      } catch (err) {
        console.log(err);
      }
    },
    showAddFile() {
      this.cancelUpload();
      this.addFile = true;
    },
    cancelUpload() {
      this.progressInfos.splice(0);
      this.selectedFiles.splice(0);
      this.mappedUploadData.splice(0);
      this.uploadData = null;
      this.inputFile = null;
    },
    selectFile() {
      this.progressInfos.splice(0);
      this.selectedFiles.splice(0);
      let allowed = this.fileTypes.split(",");
      if (event.target.files) {
        for (let i = 0; i < event.target.files.length; i++) {
          let allow = allowed.some((x) =>
            event.target.files[i].name.includes(x)
          );
          if (allow) {
            this.selectedFiles.push(event.target.files[i]);
            this.progressInfos[i] = {
              percentage: 0,
              fileName: event.target.files[i].name,
            };
          }
        }
      }
    },
    async mapAndvalidateUploadData() {
      if (!this.uploadData || !this.uploadData.data || this.uploadData.data.length === 0)
        return;

      this.mappedUploadData.splice(0);

      const mappedUploadData = this.uploadData.data.map(d => {
        const output = { isNew: true, user_id: null, useEmailForUsername: false };
        this.uploadDataHeaders.forEach(h => {
          let val = h.source ? (d[h.source] || '').trim() : '';
          const issues = [];
          let vals = [];
          if (h.source) {
            switch (h.value) {
              case "firstname":
              case "lastname":
              case "username":
                if (!val)
                  issues.push(`Missing`);
                break;
              case "email":
                if (!val)
                  issues.push(`Missing`);
                else if (!/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(val))
                  issues.push(`Invalid email address`);
                break;
              case "sso_only":
                if (val && !['1', '0', 'true', 'false', 'yes', 'no'].includes(val.toLowerCase()))
                  issues.push(`Must be one of true/false/yes/no`);
                break;
              case "permission":
                if (!val)
                  issues.push(`Missing`);
                else {
                  vals = val.toLowerCase().split('|').map(v => v.trim());
                  if (vals.some(v => !this.permissionGroups.some(pg => pg.name.toLowerCase() === v)))
                    issues.push(`Invalid permission, must be one of:<br />${this.permissionGroups.map(pg => pg.name).join(', ')}`);
                } 
                break;
              case "hierarchy_restrict":
                if (this.restrictUsersToHierarchy && val && !['1', '0', 'true', 'false', 'yes', 'no'].includes(val.toLowerCase()))
                  issues.push(`Must be one of true/false/yes/no`);
                break;
            }
            if (this.restrictUsersToHierarchy && h.value.startsWith('ht_') && val) {
              vals = val.split(/[\n|]+/);
              if (vals.some(v => !findNodeIdByDesc(v, h.ht.treePickerOptionsActive)))
                issues.push(`Must be a valid ${h.ht.label} hierarchy node`);
            }
          }
          output[h.value] = {
            value: val,
            valueArr: vals,
            issues: issues
          }
        });
        output.useEmailForUsername = this.useEmailForUsername && ['1', 'true', 'yes'].includes(output.sso_only.value?.toLowerCase());
        return output;
      });
      const usernames = [];
      usernames.push(...mappedUploadData.filter(d => d.useEmailForUsername && d.email?.value).map(d => d.email.value));
      usernames.push(...mappedUploadData.filter(d => !d.useEmailForUsername && d.username?.value).map(d => d.username.value));

      if (usernames.length !== 0)
        await axios
          .post("user/uniquenamecheckmultiple/", usernames)
          .then((resp) => {
            if (resp.data.Status === "OK") {
              resp.data.Data.forEach(u => {
                mappedUploadData
                  .filter(d => (d.useEmailForUsername && d.email?.value && u.username && d.email.value?.toLowerCase() === u.username.toLowerCase())
                          || (!d.useEmailForUsername && d.username?.value && u.username && d.username.value?.toLowerCase() === u.username.toLowerCase()))
                  .forEach(d => {
                    if (u.same_client) {
                      d.isNew = false;
                      d.user_id = u.user_id;
                    } else if (d.useEmailForUsername) {
                      d.email.issues.push('Email is already in use');  
                    } else {
                      d.username.issues.push('Username is already in use');
                    }
                  });
              })
            }
          })
          .catch((err) => {
            if (err.response && err.response.status === 401) {
              this.$emit("sessionExpired", err);
            } else {
              console.log(err);
              this.response = err.response
                ? err.response.data
                : { message: "Unexpected Error" };
            }
            this.isLoading = false;
          });

      this.mappedUploadData.push(...mappedUploadData);
    },
    isValid(item) {
      return !this.filteredUploadDataHeaders.some(h => (h.required & !h.source) || item[h.value].issues.length)
    },
    setUploadSource(head, source) {
      head.source = source;
      this.mapAndvalidateUploadData();
    },
    setUploadSort(head) {
      if (head.sortable === false) return;

      if (this.sortBy === head.value) {
        this.sortDesc = !this.sortDesc;
      } else {
        this.sortBy = head.value;
      }
    },
    sortByProperty(property) {
      return function (a, b) {
        if (a[property] > b[property]) return 1;
        else if (a[property] < b[property]) return -1;

        return 0;
      };
    },
    async createUsers() {
      this.processing = true;
      this.createProgress = 0;
      const errors = [];
      for (let index = 0; index < this.validUploadData.length; index++) {
        try {
          const row = this.validUploadData[index];
          const pgIds = row.permission.valueArr
            .map(v => this.permissionGroups.find(pg => pg.name.trim().toLowerCase() === v))
            .filter(pg => pg)
            .map(pg => pg.permission_group_id);
          const hNodes = this.restrictUsersToHierarchy ? this.hierarchies.map(ht => {
              return {
                ht_id: ht.ht_id,
                node_ids: row[`ht_${ht.ht_id}`].valueArr.map(v => findNodeIdByDesc(v, ht.treePickerOptionsActive)).filter(v => v)
              }
            }) : [];
          const ssoOnly = this.ssoEnabled && ['1', 'yes', 'true'].includes(row.sso_only.value?.toLowerCase());
          const data = {
            isNew: row.isNew,
            user_id: row.user_id,
            bulkaction: true,
            first_name: row.firstname.value,
            last_name: row.lastname.value,
            username: row.useEmailForUsername ? row.email.value : row.username.value,
            email: row.email.value,
            sso_only: ssoOnly ? 1 : 0,
            permissionGroups: pgIds,
            restrict_to_hierarchy: this.restrictUsersToHierarchy && ['1', 'yes', 'true'].includes(row.hierarchy_restrict.value.toLowerCase()) ? 1 : 0,
            hierarchy_nodes: hNodes,
            sendAccountSetupEmail: this.uploadSendEmail && !ssoOnly,
          }
          await axios.post("auth/saveUser/", data).then((resp) => {
            if (resp.data.Status !== 'OK') {
              errors.push(`Failed to create ${data.first_name} ${data.last_name}: ${resp.data.Message}`);
            }
          }).catch((err) => {
            console.log(err);
            errors.push(`Failed to create ${data.first_name} ${data.last_name}: Unexpected error`);
          });
        } catch (err) {
          console.log(err);
          errors.push(`Failure on row ${index + 1}: Unexpected error`);
        }
        this.createProgress = index + 1;
      }
      this.$emit("finished");
      if (errors.length === 0) {
        this.processing = false;
        this.close();
      } else {
        this.createErrors = errors;
      }
    }
  },
};
</script>
<style scoped lang="scss">
</style>
